import React, { useEffect, useState } from "react";
import {
  Link
} from "react-router-dom";
import {
  Plus,
  Pencil,
  Clock3
} from "lucide-react";
import * as ownerApi from "../../api/payroll/shiftRuleApi";
import * as portalApi from "../../api/payroll/shiftRulePortalApi";
import {
  Button,
  IconButton,
  Card,
  CardContent,
  Table,
  Badge,
  EmptyState
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

export default function ShiftRuleList({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const base = isPortal ? "/staff-panel/shift-rules" : "/shift-rules";
  const [list, setList] = useState([]);
  const toast = useToast();

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getAllShiftRules();
        setList(Array.isArray(res) ? res : res?.data || []);
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load");
      }
    })();
  }, [isPortal, api]);

  // ---- Presentation-only derivations below (no data/logic changes) ----

  const tableRows = list.map((s) => ({ ...s, id: s._id }));

  const columns = [
    {
      key: "name",
      header: "Name",
      render: (s) => <span className="font-medium text-neutral-900 dark:text-neutral-100">{s.name}</span>,
    },
    {
      key: "code",
      header: "Code",
      render: (s) => (
        <Badge variant="default" className="font-mono">
          {s.code}
        </Badge>
      ),
    },
    {
      key: "time",
      header: "Time",
      render: (s) => `${s.startTime} – ${s.endTime}`,
    },
    {
      key: "shiftAllowance",
      header: "Allowance",
      align: "right",
      render: (s) => `₹${Number(s.shiftAllowance || 0).toLocaleString()}`,
    },
    {
      key: "isDefault",
      header: "Default",
      render: (s) => <Badge variant={s.isDefault ? "primary" : "default"}>{s.isDefault ? "Yes" : "No"}</Badge>,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (s) => (
        <Link to={`${base}/edit/${s._id}`}>
          <IconButton aria-label="Edit shift rule" variant="ghost" size="sm">
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        </Link>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 border-b border-neutral-200 dark:border-neutral-800 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
          Shift Rules
        </h1>
        <Link to={`${base}/create`}>
          <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />}>
            Add
          </Button>
        </Link>
      </div>

      

      <Card>
        <CardContent className="p-0">
          <Table
            columns={columns}
            data={tableRows}
            emptyState={
              <EmptyState
                icon={Clock3}
                title="No shift rules"
                description="Add a shift rule to define working hours and allowances."
                action={
                  <Link to={`${base}/create`}>
                    <Button variant="primary" size="sm" leftIcon={<Plus className="h-4 w-4" />}>
                      Add
                    </Button>
                  </Link>
                }
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}